/**
 * Logger - Centralized logging utility
 *
 * Provides structured, controllable logging across the application.
 * Features:
 * - Module-prefixed logs for easy filtering
 * - Log level control (debug, info, warn, error)
 * - Environment-aware (can disable in production)
 * - Optional emoji indicators for visual scanning
 *
 * Usage:
 *   import { Logger } from '../utils/Logger';
 *   const logger = Logger.getLogger('SceneManager');
 *   logger.info('Stage loaded');
 *   logger.debug('Cache hit for stage', stageId);
 *   logger.error('Failed to load', error);
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

interface LoggerConfig {
  level: LogLevel;
  enableTimestamps: boolean;
  enableEmoji: boolean;
}

// Global configuration
const globalConfig: LoggerConfig = {
  level: 'debug', // Default to debug for development
  enableTimestamps: false,
  enableEmoji: true,
};

// Log level priority (lower = more verbose)
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

// Text indicators for each log level (using ASCII symbols for better compatibility)
const EMOJI: Record<LogLevel, string> = {
  debug: '[DBG]',
  info: '[OK]',
  warn: '[WARN]',
  error: '[ERR]',
  none: '',
};

/**
 * Logger class for module-specific logging
 */
export class Logger {
  private module: string;
  private static instances: Map<string, Logger> = new Map();

  private constructor(module: string) {
    this.module = module;
  }

  /**
   * Get or create a logger instance for a module
   * @param module The module/component name (e.g., 'SceneManager', 'UIManager')
   */
  static getLogger(module: string): Logger {
    if (!Logger.instances.has(module)) {
      Logger.instances.set(module, new Logger(module));
    }
    return Logger.instances.get(module)!;
  }

  /**
   * Set the global log level
   * @param level The minimum log level to display
   */
  static setLevel(level: LogLevel): void {
    globalConfig.level = level;
    console.log(`[Logger] Log level set to: ${level}`);
  }

  /**
   * Get the current log level
   */
  static getLevel(): LogLevel {
    return globalConfig.level;
  }

  /**
   * Enable or disable timestamps in log output
   */
  static setTimestamps(enabled: boolean): void {
    globalConfig.enableTimestamps = enabled;
  }

  /**
   * Enable or disable emoji in log output
   */
  static setEmoji(enabled: boolean): void {
    globalConfig.enableEmoji = enabled;
  }

  /**
   * Configure logger from environment or settings
   * Call this at app startup
   */
  static configure(options: Partial<LoggerConfig>): void {
    Object.assign(globalConfig, options);
  }

  /**
   * Check if a log level should be displayed
   */
  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[globalConfig.level];
  }

  /**
   * Format the log prefix
   */
  private formatPrefix(level: LogLevel): string {
    const parts: string[] = [];

    if (globalConfig.enableTimestamps) {
      const now = new Date();
      const time = now.toISOString().split('T')[1].slice(0, 12);
      parts.push(`[${time}]`);
    }

    parts.push(`[${this.module}]`);

    if (globalConfig.enableEmoji) {
      parts.push(EMOJI[level]);
    }

    return parts.join(' ');
  }

  /**
   * Log a debug message (verbose, for development)
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatPrefix('debug'), message, ...args);
    }
  }

  /**
   * Log an info message (general information)
   */
  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.log(this.formatPrefix('info'), message, ...args);
    }
  }

  /**
   * Log a warning message (potential issues)
   */
  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatPrefix('warn'), message, ...args);
    }
  }

  /**
   * Log an error message (failures)
   */
  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatPrefix('error'), message, ...args);
    }
  }

  /**
   * Log a group of related messages
   */
  group(label: string): void {
    if (this.shouldLog('debug')) {
      console.group(`[${this.module}] ${label}`);
    }
  }

  /**
   * End a log group
   */
  groupEnd(): void {
    if (this.shouldLog('debug')) {
      console.groupEnd();
    }
  }

  /**
   * Log a table (useful for arrays/objects)
   */
  table(data: unknown): void {
    if (this.shouldLog('debug')) {
      console.log(`[${this.module}]`);
      console.table(data);
    }
  }

  /**
   * Time an operation
   */
  time(label: string): void {
    if (this.shouldLog('debug')) {
      console.time(`[${this.module}] ${label}`);
    }
  }

  /**
   * End timing an operation
   */
  timeEnd(label: string): void {
    if (this.shouldLog('debug')) {
      console.timeEnd(`[${this.module}] ${label}`);
    }
  }
}

// Export a default logger for quick use
export const logger = Logger.getLogger('App');

// Expose logger configuration globally for debugging
if (typeof window !== 'undefined') {
  window.setLogLevel = (level: string) => {
    Logger.setLevel(level as LogLevel);
  };
  window.getLogLevel = () => Logger.getLevel();
}
